import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { User, Role } from 'app/auth/models';
import { ToastrService } from 'ngx-toastr';
import { getConnectedEnterpriseNameFromUser } from 'app/services/enterprise-context';
import {
  clearErpAuthSession,
  ErpLoginResponse,
  getErpJwtToken,
  persistErpLoginSession,
} from 'app/services/auth/erp-auth-token.util';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { ensureHttpsUrl } from 'app/auth/helpers/ssl-auth.util';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  public currentUser: Observable<User>;

  private currentUserSubject: BehaviorSubject<User>;

  constructor(
    private _http: HttpClient,
    private _toastrService: ToastrService,
    private _logicielCaisseAuth: LogicielCaisseAuthService
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(this.readStoredUser());
    this.currentUser = this.currentUserSubject.asObservable();
  }

  private readStoredUser(): User | null {
    try {
      const raw = localStorage.getItem('currentUser');
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      localStorage.removeItem('currentUser');
      return null;
    }
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  get isAdmin() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Admin;
  }

  get isClient() {
    return this.currentUser && this.currentUserSubject.value.role === Role.Client;
  }

  login(email: string, password: string) {
    const authUrl = ensureHttpsUrl(
      `${String(environment.apiUrl || '').replace(/\/$/, '')}/users/authenticate`
    );
    return this._http.post<any>(authUrl, { email, password }).pipe(
        map((user) => {
          if (user && user.token) {
            this.applyLoginSession({ user, token: user.token });
            setTimeout(() => {
              const entreprise = getConnectedEnterpriseNameFromUser(user);
              this._toastrService.success(
                'Vous êtes connecté en tant que ' +
                  user.role +
                  (entreprise ? ` — ${entreprise}` : '') +
                  '.',
                '👋 Bienvenue, ' + user.firstName + '!',
                { toastClass: 'toast ngx-toastr', closeButton: true }
              );
            }, 2500);
          }

          return user;
        })
      );
  }

  applyLoginSession(data: ErpLoginResponse): void {
    const nextUserId = Number(data.user?.id);
    let previousUserId: number | null = null;
    try {
      const prev = JSON.parse(localStorage.getItem('currentUser') || 'null');
      const id = Number(prev?.id);
      previousUserId = Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      previousUserId = null;
    }

    if (previousUserId && nextUserId && previousUserId !== nextUserId) {
      this._logicielCaisseAuth.clearSession();
      this._logicielCaisseAuth.invalidateAccessCache();
    }

    this._logicielCaisseAuth.resetBootstrapForNewErpSession();

    persistErpLoginSession(data);
    const jwt = getErpJwtToken();
    if (data.user) {
      const user = { ...data.user, ...(jwt ? { token: jwt } : {}) } as User;
      this.currentUserSubject.next(user);
    }

    // Ne pas lancer autoConnect ici : la page /login appelle authenticateAfterErpLogin
    // (évite course bootstrap → faux needs_setup). Warmup reste dans app.component.
  }

  logout() {
    this._logicielCaisseAuth.clearSession();
    this._logicielCaisseAuth.invalidateAccessCache();
    clearErpAuthSession();
    this.currentUserSubject.next(null);
  }
}
